// CANVAS-VOICE: the person's own hand on their Live canvas (the voice front, services/voice, acting
// as the person). The same op contract as MCP `show_user` (canvas.ts `CanvasOp`), written for one
// Live session only (never company-wide: a person never places blocks on everyone's canvas), and
// a show keeps only the blocks the caller may read itself. Every viewer of the session is then
// served only what it may read, as for an agent's op. Kept apart from canvas.ts so the block
// work on that file merges cleanly.
import { z } from "zod";
import { Uuid } from "../primitives";
import {
  CanvasArrangeOp,
  CanvasClearOp,
  CanvasFocusViewOp,
  CanvasHighlightOp,
  CanvasOpResult,
  CanvasPlaceOp,
  CanvasShowOp,
  CanvasTidyOp,
} from "./canvas";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";

/**
 * The ops a person's own hand may write: every `CanvasOp` but `interact` (a click record has its
 * own route, `liveCanvas.interact`, which checks the block and records the membership).
 */
export const LiveCanvasPersonOp = z
  .discriminatedUnion("op", [
    CanvasShowOp,
    CanvasArrangeOp,
    CanvasHighlightOp,
    CanvasClearOp,
    CanvasFocusViewOp,
    // LIVE-DESKTOP: "put that away", and windows' geometry.
    CanvasTidyOp,
    CanvasPlaceOp,
  ])
  .meta({ id: "LiveCanvasPersonOp" });

export const LiveCanvasOpBody = z.strictObject({
  /** The Live session (the Live page's tab) the op is for. */
  session_id: Uuid,
  op: LiveCanvasPersonOp,
});
export type LiveCanvasOpBody = z.infer<typeof LiveCanvasOpBody>;

export const canvasVoiceRoutes = {
  "liveCanvas.op": defineRoute({
    feature: "core",
    id: "liveCanvas.op",
    method: "post",
    path: `${base}/live/canvas/ops`,
    tag: "live",
    summary: "Show, arrange, highlight or clear blocks on the caller's own Live session",
    description:
      "`show_user` for a person (the voice front calls it with the person's credential): one " +
      "op for one Live session. A show keeps only the blocks the caller may read; none left is " +
      "404. 404 while the company's `live_canvas` switch is off. Sessions only (no keys).",
    access: "viewer",
    params: CompanyParams,
    body: LiveCanvasOpBody,
    response: { status: 201, schema: CanvasOpResult },
    errors: [],
    emits: ["canvas.op_written"],
  }),
} as const;
